import { columns } from "@/components/jobs/jobs.columns";
import { DataTable } from "@/components/table/table";
import { useDeleteEvent } from "@/hooks/useDeleteModal";
import { useJobsModal } from "@/hooks/useJobsModal";
import { useTRPC } from "@/lib/trpc";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "../ui/button";
import { ConfirmModal } from "../ui/confirm-modal";
import { JobsModalForm } from "./jobs-modal-form";

const jobFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  department: z.string().optional(),
  category: z.string().optional(),
  salaryRange: z.string().optional(),
  requirements: z.string().optional(),
  responsibilities: z.string().optional(),
  qualifications: z.string().optional(),
  contactInfo: z.string().optional(),
  applicationUrl: z.string().optional(),
  status: z.enum(["active", "closed", "draft"]).default("active"),
  postedDate: z.date(),
  expirationDate: z.date().optional(),
  featuredImageId: z.string().optional(),
  attachmentId: z.string().optional(),
});

type JobFormValues = z.infer<typeof jobFormSchema>;

export function JobsTable() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { data: jobs = [], isLoading } = useQuery(
    trpc.jobs.list.queryOptions(),
  );

  const deleteId = useDeleteEvent((s) => s.toDeleteId);
  const setDeleteId = useDeleteEvent((s) => s.setToDeleteId);

  const isModalOpen = useJobsModal((s) => s.isOpen);
  const setIsModalOpen = useJobsModal((s) => s.setIsModalOpen);
  const setJobId = useJobsModal((s) => s.setJobId);
  const jobId = useJobsModal((s) => s.jobId);
  const defaultValues = useJobsModal((s) => s.defaultValues);
  const setDefaultValues = useJobsModal((s) => s.setDefaultValues);

  const form = useForm<JobFormValues>({
    resolver: zodResolver(jobFormSchema),
    defaultValues: {
      title: "",
      description: "",
      department: "",
      category: "",
      salaryRange: "",
      requirements: "",
      responsibilities: "",
      qualifications: "",
      contactInfo: "",
      applicationUrl: "",
      status: "active",
      postedDate: new Date(),
      expirationDate: undefined,
      featuredImageId: undefined,
      attachmentId: undefined,
    },
  });

  useEffect(() => {
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      form.reset({
        title: defaultValues.title || "",
        description: defaultValues.description || "",
        department: defaultValues.department || "",
        category: defaultValues.category || "",
        salaryRange: defaultValues.salaryRange || "",
        requirements: defaultValues.requirements || "",
        responsibilities: defaultValues.responsibilities || "",
        qualifications: defaultValues.qualifications || "",
        contactInfo: defaultValues.contactInfo || "",
        applicationUrl: defaultValues.applicationUrl || "",
        status: defaultValues.status || "active",
        postedDate: defaultValues.postedDate || new Date(),
        expirationDate: defaultValues.expirationDate,
        featuredImageId: defaultValues.featuredImageId,
        attachmentId: defaultValues.attachmentId,
      });
    }
  }, [form, defaultValues]);

  const deleteMutation = useMutation(
    trpc.jobs.delete.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["jobs"] });
        setDeleteId(null);
        toast.success("Job deleted successfully");
      },
      onError: (error: unknown) => {
        toast.error((error as Error).message || "Failed to delete");
      },
    }),
  );

  const createMutation = useMutation(
    trpc.jobs.create.mutationOptions({
      onSuccess: () => {
        form.reset();
        queryClient.invalidateQueries({ queryKey: ["jobs"] });
        setJobId(null);
        toast.success("Job created successfully");
        setIsModalOpen(false);
      },
      onError: (error: unknown) => {
        toast.error((error as Error).message || "Failed to create");
      },
    }),
  );

  const updateMutation = useMutation(
    trpc.jobs.update.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["jobs"] });
        setJobId(null);
        setIsModalOpen(false);
        form.reset();
        toast.success("Job updated successfully");
      },
      onError: (error: unknown) => {
        toast.error((error as Error).message || "Failed to update");
      },
    }),
  );

  const handleSubmit = (data: JobFormValues) => {
    if (jobId) {
      updateMutation.mutate({ ...data, id: jobId });
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <div className="flex flex-1 p-8 justify-around gap-8">
      <div className="flex flex-col space-y-8 w-full xl:w-3/4 lg:max-w-4xl xl:max-w-6xl">
        <div className="flex items-center justify-between space-y-2">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Job Listings</h2>
            <p className="text-muted-foreground">
              Manage job listings for the website
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              onClick={() => {
                setDefaultValues({});
                form.reset({
                  title: "",
                  description: "",
                  department: "",
                  category: "",
                  salaryRange: "",
                  requirements: "",
                  responsibilities: "",
                  qualifications: "",
                  contactInfo: "",
                  applicationUrl: "",
                  status: "active",
                  postedDate: new Date(),
                  expirationDate: undefined,
                  featuredImageId: undefined,
                  attachmentId: undefined,
                });
                setIsModalOpen(true);
              }}
            >
              New Job
            </Button>
          </div>
        </div>

        <JobsModalForm
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          form={form}
          onSubmit={handleSubmit}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />

        <ConfirmModal
          open={Boolean(deleteId)}
          onConfirm={() => {
            if (deleteId) deleteMutation.mutate({ id: deleteId });
          }}
          onDismiss={() => {
            setDeleteId(null);
          }}
          isLoading={deleteMutation.isPending}
        />

        {!isLoading && (
          <DataTable data={jobs} columns={columns} filterColumn="title" />
        )}
      </div>
    </div>
  );
}
